import React, { useState } from "react";
import { ref, push } from "firebase/database";
import { db } from "../firebase";
import { isValidVideoUrl, extractDriveFileId } from "../utils/driveImporter";

const DriveBulkImport = ({ activeCourse, onClose, onImportComplete }) => {
  const [simpleInput, setSimpleInput] = useState("");
  const [chapterName, setChapterName] = useState("");
  const [showScript, setShowScript] = useState(false);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState("");
  const [importing, setImporting] = useState(false);
  const [importedCount, setImportedCount] = useState(0);
  
  const extractionScript = `// Google Drive Video Extractor
// Run this in the browser console while viewing your Google Drive chapter folder

(async function() {
  console.log('🎬 Starting video extraction...');
  const videos = [];
  const processed = new Set();
  
  function extractFromPage() {
    const selectors = ['[data-id][data-name]', '[data-id]', '[role="gridcell"]', '[role="row"]', '[jsname]'];
    let items = [];
    for (const selector of selectors) {
      items = Array.from(document.querySelectorAll(selector));
      if (items.length > 0) break;
    }
    
    items.forEach(item => {
      let fileId = item.getAttribute('data-id') || item.getAttribute('data-target-id') || item.querySelector('[data-id]')?.getAttribute('data-id');
      if (!fileId || processed.has(fileId)) return;
      
      const nameElement = item.querySelector('[data-name]') || item;
      let fileName = nameElement.getAttribute('data-name') || nameElement.getAttribute('data-title') || nameElement.getAttribute('title');
      
      if (!fileName || fileName.length < 3) {
        const textContent = item.textContent?.trim();
        if (textContent && textContent.match(/\\.(mp4|avi|mov|mkv|webm|m4v|wmv|flv)$/i)) {
          fileName = textContent;
        }
      }
      
      if (!fileName || fileName.length < 3) {
        fileName = item.getAttribute('aria-label') || item.querySelector('[aria-label]')?.getAttribute('aria-label');
      }
      
      if (!fileName) return;
      
      fileName = fileName.replace(/Shared$/i, '').replace(/\\s+/g, ' ').trim();
      
      if (fileName && /\.(mp4|avi|mov|mkv|webm|m4v|wmv|flv)$/i.test(fileName)) {
        processed.add(fileId);
        const previewUrl = \`https://drive.google.com/file/d/\${fileId}/preview\`;
        const videoTitle = fileName.replace(/\\.(mp4|avi|mov|mkv|webm|m4v|wmv|flv)$/i, '').trim();
        
        videos.push({ title: videoTitle, url: previewUrl });
        console.log(\`✅ Found: \${videoTitle}\`);
      }
    });
  }
  
  await new Promise(resolve => setTimeout(resolve, 1000));
  extractFromPage();
  console.log(\`Initial scan found \${videos.length} videos\`);
  
  const container = document.querySelector('[role="main"]') || document.querySelector('.a-s-fa-Ha-pa') || document.body;
  
  if (container) {
    let lastScroll = container.scrollTop || 0;
    let scrollAttempts = 0;
    let noNewVideosCount = 0;
    
    const scrollInterval = setInterval(() => {
      container.scrollTop += 800;
      scrollAttempts++;
      
      setTimeout(() => {
        const beforeCount = videos.length;
        extractFromPage();
        const afterCount = videos.length;
        
        if (beforeCount === afterCount) noNewVideosCount++;
        else noNewVideosCount = 0;
        
        if (scrollAttempts > 100 || (container.scrollTop === lastScroll && noNewVideosCount > 3) || (scrollAttempts > 20 && noNewVideosCount > 5)) {
          clearInterval(scrollInterval);
          finish();
        }
        lastScroll = container.scrollTop;
      }, 500);
    }, 800);
    
    setTimeout(() => {
      clearInterval(scrollInterval);
      finish();
    }, 60000);
  } else {
    finish();
  }
  
  function finish() {
    console.log(\`\\n🎉 Found \${videos.length} videos\\n\`);
    
    if (videos.length === 0) {
      console.log('❌ No videos found. Make sure you\\'re viewing a folder with video files.');
      return;
    }
    
    let output = '';
    videos.forEach(v => {
      output += \`\${v.title}\\n\${v.url}\\n\\n\`;
    });
    
    console.log('\\n=== COPY FROM HERE ===\\n');
    console.log(output.trim());
    console.log('\\n=== COPY TO HERE ===\\n');
    
    const copyToClipboard = async () => {
      try {
        await navigator.clipboard.writeText(output.trim());
        console.log('✅ Copied to clipboard!');
      } catch (err) {
        const textarea = document.createElement('textarea');
        textarea.value = output.trim();
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand('copy');
          console.log('✅ Copied!');
        } catch (e) {
          console.log('⚠️ Please manually copy the text above.');
        }
        document.body.removeChild(textarea);
      }
    };
    
    copyToClipboard();
  }
})();`;

  const copyScript = () => {
    navigator.clipboard.writeText(extractionScript).then(() => {
      alert('✅ Script copied to clipboard!');
    }).catch(() => {
      const textarea = document.createElement('textarea');
      textarea.value = extractionScript;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      alert('✅ Script copied to clipboard!');
    });
  };

  const handleScanFolder = async () => {
    if (!chapterName.trim()) {
      setError("Please enter a chapter name");
      return;
    }

    if (!simpleInput.trim()) {
      setError("Please run the extraction script and paste the video titles and links above.");
      return;
    }

    setLoading(true);
    setError("");
    setProgress("Parsing extracted videos...");

    try {
      const lines = simpleInput.split('\n').map(line => line.trim()).filter(line => line);
      const parsedVideos = [];
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        
        if (line.toLowerCase().startsWith('chapter:')) {
          continue;
        }
        
        if (isValidVideoUrl(line)) {
          if (i > 0 && !isValidVideoUrl(lines[i - 1])) {
            const title = lines[i - 1];
            parsedVideos.push({
              title: title,
              url: line,
              chapter: chapterName.trim(),
              published: true
            });
          } else {
            parsedVideos.push({
              title: `Video ${parsedVideos.length + 1}`,
              url: line,
              chapter: chapterName.trim(),
              published: true
            });
          }
        }
      }
      
      if (parsedVideos.length === 0) {
        setError("No valid videos found. Make sure the format is: Title on one line, URL on the next line.");
      } else {
        setVideos(parsedVideos);
        setProgress(`Found ${parsedVideos.length} videos for chapter "${chapterName}"`);
        setError("");
      }
    } catch (err) {
      setError(`Error parsing videos: ${err.message}`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (videos.length === 0) {
      setError("No videos to import. Please scan a folder first.");
      return;
    }

    setImporting(true);
    setError("");
    setImportedCount(0);

    try {
      let successCount = 0;
      let skippedCount = 0;
      
      for (let i = 0; i < videos.length; i++) {
        const video = videos[i];
        
        if (!isValidVideoUrl(video.url)) {
          skippedCount++;
          setProgress(`Skipped ${skippedCount} invalid videos, imported ${successCount}/${videos.length}...`);
          continue;
        }

        let finalUrl = video.url;
        if (video.url.includes("drive.google.com") && !video.url.includes("/preview")) {
          const fileId = extractDriveFileId(video.url);
          if (fileId) {
            finalUrl = `https://drive.google.com/file/d/${fileId}/preview`;
          }
        }

        const videoData = {
          title: video.title.trim(),
          url: finalUrl.trim(),
          chapter: video.chapter || "Uncategorized",
          published: video.published !== false
        };

        await push(ref(db, `courses/${activeCourse}/videos`), videoData);
        successCount++;
        setImportedCount(successCount);
        setProgress(`Imported ${successCount}/${videos.length} videos...`);
      }

      const finalMessage = skippedCount > 0 
        ? `Successfully imported ${successCount} videos (${skippedCount} skipped)!`
        : `Successfully imported ${successCount} videos!`;
      
      setProgress(finalMessage);
      if (onImportComplete) {
        onImportComplete(successCount);
      }
      
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      setError(`Error importing videos: ${err.message}`);
      console.error(err);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Bulk Import from Google Drive</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          </div>

          {/* Chapter Name Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Chapter Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={chapterName}
              onChange={(e) => setChapterName(e.target.value)}
              placeholder="e.g., Topic 1 Coordinate Geometry"
              className="w-full p-2 border rounded-md text-gray-900"
            />
            <p className="text-xs text-gray-500 mt-1">
              All videos will be added to this chapter.
            </p>
          </div>

          {/* Instructions */}
          <div className="bg-teal-50 border border-teal-200 rounded p-4 mb-4">
            <p className="text-sm text-teal-800 font-semibold mb-3">
              📋 How to Extract Videos:
            </p>
            <ol className="text-xs text-teal-700 list-decimal list-inside space-y-1.5">
              <li>Open your Google Drive chapter folder in a new tab</li>
              <li>Press <strong>F12</strong> → Click <strong>"Console"</strong> tab</li>
              <li>Click <strong>"Copy Script"</strong> below, then paste it in the console and press Enter</li>
              <li>Wait for extraction (it will scroll and find all videos)</li>
              <li>Copy the output from the console and paste it below</li>
            </ol>
            
            <div className="mt-3 flex gap-2">
              <button
                onClick={copyScript}
                className="text-sm bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700"
              >
                📋 Copy Script
              </button>
              <button
                onClick={() => setShowScript(!showScript)}
                className="text-sm bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
              >
                {showScript ? "Hide" : "Show"} Script
              </button>
            </div>
            
            {showScript && (
              <div className="mt-3">
                <textarea
                  readOnly
                  value={extractionScript}
                  className="w-full p-3 bg-gray-100 rounded text-xs font-mono"
                  rows="15"
                  onClick={(e) => e.target.select()}
                />
              </div>
            )}
          </div>

          {/* Extracted Videos Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Extracted Videos (paste the output from the script here)
            </label>
            <textarea
              value={simpleInput}
              onChange={(e) => setSimpleInput(e.target.value)}
              placeholder={`Coordinate Geometry Explanation
https://drive.google.com/file/d/1z8rTgESlTVArZjVnenlyxOm2hhKMN7R-/preview

Coordinate Geometry Practice
https://drive.google.com/file/d/1m5dNGTF-nCg5GpY1RiovzCV6Iu73VZ9y/preview`}
              className="w-full p-3 border rounded-md text-gray-900 font-mono text-sm"
              rows="10"
            />
            <p className="text-xs text-gray-500 mt-1">
              Format: Video title on one line, URL on the next line, blank line between videos
            </p>
          </div>

          {/* Error and Progress Messages */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}
          {progress && (
            <div className="mb-4 p-3 bg-teal-100 border border-teal-400 text-teal-700 rounded">
              {progress}
            </div>
          )}

          {/* Scanned Videos Preview */}
          {videos.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Found {videos.length} videos
              </h3>
              <div className="max-h-64 overflow-y-auto border rounded p-3 bg-gray-50">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Title</th>
                      <th className="text-left p-2">Chapter</th>
                      <th className="text-left p-2">URL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {videos.map((video, index) => (
                      <tr key={index} className="border-b">
                        <td className="p-2 text-gray-900">{video.title}</td>
                        <td className="p-2 text-gray-600">{video.chapter}</td>
                        <td className="p-2 text-gray-600 truncate max-w-xs">
                          {video.url}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
              disabled={importing}
            >
              Cancel
            </button>
            <button
              onClick={handleScanFolder}
              className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700"
              disabled={loading || importing}
            >
              {loading ? "Scanning..." : "Scan Folder"}
            </button>
            {videos.length > 0 && (
              <button
                onClick={handleImport}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                disabled={importing || loading}
              >
                {importing ? `Importing... (${importedCount}/${videos.length})` : `Import ${videos.length} Videos`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriveBulkImport;
